import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy, 
  deleteDoc, 
  doc,
  getDoc 
} from 'firebase/firestore';
import { Collections, getCollection } from './collections';
import { handleFirebaseError } from './errors';
import { FIREBASE_DB } from './config';
import type { Job, JobEmailRequest } from '../../types/jobs';

export async function addJob(jobData: Omit<Job, 'id'>) {
  try {
    const jobsCollection = getCollection(Collections.JOBS);
    const docRef = await addDoc(jobsCollection, {
      ...jobData,
      createdAt: new Date().toISOString()
    });
    return { id: docRef.id, error: null };
  } catch (error) {
    const handledError = handleFirebaseError(error);
    console.error('Error adding job:', handledError);
    return { id: null, error: handledError };
  }
}

export async function getJobs() {
  try {
    const jobsCollection = getCollection(Collections.JOBS);
    const q = query(jobsCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const jobs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Job[];

    return { jobs, error: null };
  } catch (error) {
    const handledError = handleFirebaseError(error);
    console.error('Error getting jobs:', handledError);
    return { jobs: [], error: handledError };
  }
}

export async function getJob(jobId: string) {
  try {
    const jobDoc = await getDoc(doc(FIREBASE_DB, Collections.JOBS, jobId));
    if (!jobDoc.exists()) {
      throw new Error('Job not found');
    }
    const job = { id: jobDoc.id, ...jobDoc.data() } as Job;
    return { job, error: null };
  } catch (error) {
    const handledError = handleFirebaseError(error);
    console.error('Error getting job:', handledError);
    return { job: null, error: handledError };
  }
}

export async function deleteJob(jobId: string) {
  try {
    const jobRef = doc(FIREBASE_DB, Collections.JOBS, jobId);
    await deleteDoc(jobRef);
    return { error: null };
  } catch (error) {
    const handledError = handleFirebaseError(error);
    console.error('Error deleting job:', handledError);
    return { error: handledError };
  }
}

export async function requestJobDetails(jobId: string, email: string) {
  try {
    const emailRequestsCollection = collection(FIREBASE_DB, Collections.JOBS, jobId, 'emailRequests');
    const docRef = await addDoc(emailRequestsCollection, {
      jobId,
      email,
      createdAt: new Date().toISOString(),
      sent: false
    });
    
    return { id: docRef.id, error: null };
  } catch (error) {
    const handledError = handleFirebaseError(error);
    console.error('Error requesting job details:', handledError);
    return { id: null, error: handledError };
  }
}

export async function getEmailRequests(jobId: string) {
  try {
    const emailRequestsCollection = collection(FIREBASE_DB, Collections.JOBS, jobId, 'emailRequests');
    const q = query(emailRequestsCollection, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    const requests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as JobEmailRequest[];
    
    return { requests, error: null };
  } catch (error) {
    const handledError = handleFirebaseError(error);
    console.error('Error getting email requests:', handledError);
    return { requests: [], error: handledError };
  }
}